import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { SXDropdown, type NGDropdownListItem } from "@common/components/ui-components/SXDropdown";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { selectAllMaterials, selectSections } from "../features/materialsSections/materialSectionSlice";
import { selectToken } from "../features/user/userSlice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { ProjectSlice, selectProject } from "../features/projects/projectSlice";
import { ng } from "@common/ngds";
import {
  collectDesignDatabases,
  getCachedAutoDesign,
  getElementGuid,
  getElementSectionGuid,
  parseSectionDimensions,
  type RunMemberDesign,
} from "../helpers/memberDesign";
import { RATIO_TO_PERCENT_UNIT, DesignRunButton } from "./SXMemberDesignShared";

interface ConcreteDesignSettingsProps {
  isDesigning: boolean;
  runDesign: RunMemberDesign;
}

interface SectionItem {
  guid: string;
  name: string;
  familyName?: string;
}

const CONCRETE_GRADES = ["C20/25", "C25/30", "C30/37", "C35/45", "C40/50"];
const BAR_DIAMETERS_MM = [8, 10, 12, 14, 16, 20, 25, 32];
const STIRRUP_DIAMETERS_MM = [6, 8, 10, 12];

const toDiameterLabel = (diameterMm: number) => `Ø${diameterMm}`;

/**
 * Auto-design settings for a reinforced concrete column: the section size range to search, the
 * concrete grades to try, the reinforcement the design may use and the target utilization. The
 * candidate sections are derived from the size range rather than picked one by one — concrete
 * section names are their dimensions, so the range maps directly onto the library.
 */
export const ConcreteDesignSettings = ({ isDesigning, runDesign }: ConcreteDesignSettingsProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const sections = useAppSelector(selectSections);
  const allMaterials = useAppSelector(selectAllMaterials) as Record<string, { guid: string; name: string }[]>;
  const token = useAppSelector(selectToken);
  const selProject = useAppSelector(selectProject);
  const hasRestoredRef = useRef(false);

  // Mirrors autoDesign.settings[0].limitUtilization for the panel input.
  const [limitUtilization, setLimitUtilization] = useState(1);
  const [selectedGrades, setSelectedGrades] = useState<string[]>(["C30/37"]);
  const [selectedBars, setSelectedBars] = useState<number[]>([16]);
  const [selectedStirrups, setSelectedStirrups] = useState<number[]>([8]);
  const [symmetry, setSymmetry] = useState<"uniaxial" | "biaxial">("biaxial");

  const [minY, setMinY] = useState(200);
  const [maxY, setMaxY] = useState(400);
  const [minZ, setMinZ] = useState(200);
  const [maxZ, setMaxZ] = useState(400);
  const [maxBarsY, setMaxBarsY] = useState(8);
  const [maxBarsZ, setMaxBarsZ] = useState(8);

  // Restore auto-design settings from endpointCache once, after project data first loads
  useEffect(() => {
    if (hasRestoredRef.current) return;
    const cached = getCachedAutoDesign(selProject);
    if (!cached) return;

    const concrete = cached.settings?.[0]?.concrete;
    if (!concrete) return;

    // Need materials list loaded to map GUIDs back to names
    const concreteMaterials = allMaterials?.Concrete ?? [];
    if (concrete.concLst?.length && concreteMaterials.length === 0) return;

    hasRestoredRef.current = true;
    if (concrete.concLst?.length) {
      // concLst contains GUIDs — map back to display names
      const gradeNames = concrete.concLst
        .map((guid) => concreteMaterials.find((m) => m.guid === guid)?.name)
        .filter((name): name is string => !!name);
      if (gradeNames.length > 0) setSelectedGrades(gradeNames);
    }
    if (concrete.longDia?.length) setSelectedBars(concrete.longDia as number[]);
    if (concrete.linkDia?.length) setSelectedStirrups(concrete.linkDia as number[]);
    if (concrete.maxNumberOfBarsY) setMaxBarsY(Number(concrete.maxNumberOfBarsY));
    if (concrete.maxNumberOfBarsZ) setMaxBarsZ(Number(concrete.maxNumberOfBarsZ));
    if (concrete.minSectionWidth) setMinY(Number(concrete.minSectionWidth) * 1000);
    if (concrete.maxSectionWidth) setMaxY(Number(concrete.maxSectionWidth) * 1000);
    if (concrete.minSectionHeight) setMinZ(Number(concrete.minSectionHeight) * 1000);
    if (concrete.maxSectionHeight) setMaxZ(Number(concrete.maxSectionHeight) * 1000);
    // Restore utilization threshold from cached auto-design settings when available.
    const cachedLimitUtilization = cached.settings?.[0]?.limitUtilization;
    if (typeof cachedLimitUtilization === "number" && Number.isFinite(cachedLimitUtilization)) {
      setLimitUtilization(cachedLimitUtilization);
    }
    if (concrete.detailingOptions?.length) {
      // detailingOptions are serialised as strings by toObject() (e.g. 'DETAILING_OPTION_BIAXIAL_SYMMETRY')
      const hasBiaxial = concrete.detailingOptions.some(
        (opt) =>
          ng.endpoint_args.common_api_functions.DetailingOption_FromObject(opt) ===
          ng.endpoint_args.common_api_functions.DetailingOption.DETAILING_OPTION_BIAXIAL_SYMMETRY,
      );
      setSymmetry(hasBiaxial ? "biaxial" : "uniaxial");
    }
  }, [selProject?.data, allMaterials]);

  const allSections = useMemo<SectionItem[]>(() => {
    return (Object.values(sections ?? {}) as SectionItem[][]).flat();
  }, [sections]);

  const activeSectionGuid = getElementSectionGuid(selProject?.data);

  const activeSection = useMemo(
    () => allSections.find((section) => section.guid === activeSectionGuid),
    [allSections, activeSectionGuid],
  );

  const isCircularConcreteSection = activeSection?.familyName === "C";

  useEffect(() => {
    if (isCircularConcreteSection && symmetry !== "biaxial") {
      setSymmetry("biaxial");
    }
  }, [isCircularConcreteSection, symmetry]);

  useEffect(() => {
    if (isCircularConcreteSection) {
      return;
    }

    if (symmetry === "biaxial" && maxBarsZ !== maxBarsY) {
      setMaxBarsZ(maxBarsY);
    }
  }, [isCircularConcreteSection, symmetry, maxBarsY, maxBarsZ]);

  // Options for symmetry dropdown
  const symmetryOptions: NGDropdownListItem<"uniaxial" | "biaxial">[] = useMemo(
    () =>
      isCircularConcreteSection
        ? [{ id: "biaxial", name: t("biaxial_symmetry_.label", "Biaxial symmetry") }]
        : [
            { id: "uniaxial", name: t("uniaxial_symmetry_.label", "Uniaxial symmetry") },
            { id: "biaxial", name: t("biaxial_symmetry_.label", "Biaxial symmetry") },
          ],
    [isCircularConcreteSection, t],
  );
  // Ensure selected symmetry option is valid
  const selectedSymmetry = useMemo(
    () => symmetryOptions.find((option) => option.id === symmetry) ?? symmetryOptions[1],
    [symmetry, symmetryOptions],
  );

  // Filter sections based on dimension limits
  const matchingSections = useMemo<SectionItem[]>(() => {
    return allSections.filter((section) => {
      if (isCircularConcreteSection) {
        if (section.familyName !== "C") return false;
      } else {
        // Only allow rectangular (R) and square (S) sections
        if (section.familyName !== "R" && section.familyName !== "S") return false;
      }

      const dims = parseSectionDimensions(section.name);
      if (!dims) return false;
      const isWithinDimensionRange = isCircularConcreteSection
        ? dims.y >= minY && dims.y <= maxY
        : dims.y >= minY && dims.y <= maxY && dims.z >= minZ && dims.z <= maxZ;
      if (!isWithinDimensionRange) return false;
      // Allow both rectangular and square sections for both symmetry options
      return true;
    });
  }, [allSections, isCircularConcreteSection, minY, maxY, minZ, maxZ]);

  // Helper to toggle selection in a multi-select list
  const toggleMultiSelect = <T extends string | number>(
    value: T,
    selectedValues: T[],
    onUpdate: (values: T[]) => void,
  ) => {
    if (selectedValues.includes(value)) {
      onUpdate(selectedValues.filter((item) => item !== value));
      return;
    }

    onUpdate([...selectedValues, value]);
  };

  // Render a selectable chip (button) for a given item
  const renderSelectableChip = <T extends string | number>(
    id: T,
    label: string,
    selectedValues: T[],
    onUpdate: (values: T[]) => void,
    disabled = false,
  ) => (
    <Button
      key={String(id)}
      size="sm"
      disabled={disabled}
      className={`sx-cd-chip sx-cd-chip--selectable ${selectedValues.includes(id) ? "is-selected" : ""}`}
      onClick={() => toggleMultiSelect(id, selectedValues, onUpdate)}
      aria-pressed={selectedValues.includes(id)}
    >
      {label}
    </Button>
  );
  // Render a non-interactive chip for display purposes
  const renderDisplayChip = (id: string, label: string) => (
    <span key={id} className="sx-cd-chip sx-cd-chip--display" aria-hidden="true">
      {label}
    </span>
  );

  const buildAutoDesign = async () => {
    const elementJson = selProject?.data?.data?.input?.structures?.[0]?.elements?.[0] ?? null;
    const element: ng.element.Data | null = elementJson ? ng.element.Data.fromObject(elementJson) : null;
    const beamObj = element?.getBeam() ?? null;
    const concreteData = beamObj?.getConcrete() ?? null;
    const rebarMtrlGuid = concreteData?.rebars?.grps?.[0]?.mtrlGuid ?? "";
    const stirrupMtrlGuid = concreteData?.links?.linkGrps?.[0]?.mtrlGuid ?? "";

    // Map grade names to GUIDs from the materials list
    const concreteMaterials = allMaterials?.Concrete ?? [];
    const gradeGuids = selectedGrades
      .map((name) => concreteMaterials.find((m) => m.name === name)?.guid)
      .filter((guid): guid is string => !!guid);

    // Every material GUID the reference database must carry (concrete grades + the reinforcement
    // materials the element references).
    const materialGuids = [
      ...new Set([
        ...gradeGuids,
        ...(rebarMtrlGuid ? [rebarMtrlGuid] : []),
        ...(stirrupMtrlGuid ? [stirrupMtrlGuid] : []),
      ]),
    ];
    const sectionGuids = matchingSections.map((s) => s.guid);

    const { secDb, mtrlDb } = await collectDesignDatabases({
      project: selProject,
      token,
      sectionGuids,
      materialGuids,
    });

    // Preserve fields managed by the server (distances/covers, spacing params, etc.)
    // from the existing cached concrete settings — only override fields the UI owns.
    const cachedConcrete = getCachedAutoDesign(selProject)?.settings?.[0]?.concrete;

    const concreteSettings = ng.endpoint_args.common_api_functions.AutoDesignConcrete.create({
      // Preserved from server defaults — do not overwrite
      ...(cachedConcrete?.distances?.length
        ? {
            distances: cachedConcrete.distances.map((d) => ng.design.concrete.CoverAndSpace.fromObject(d)),
          }
        : {}),
      ...(cachedConcrete?.coverDeviation !== undefined
        ? { coverDeviation: Number(cachedConcrete.coverDeviation) }
        : {}),
      ...(cachedConcrete?.vibSpace !== undefined ? { vibSpace: Number(cachedConcrete.vibSpace) } : {}),
      ...(cachedConcrete?.maxAggregateSize !== undefined
        ? { maxAggregateSize: Number(cachedConcrete.maxAggregateSize) }
        : {}),
      ...(cachedConcrete?.regularSpacing !== undefined ? { regularSpacing: cachedConcrete.regularSpacing } : {}),
      ...(cachedConcrete?.freeSpaceBetweenStirrups !== undefined
        ? { freeSpaceBetweenStirrups: Number(cachedConcrete.freeSpaceBetweenStirrups) }
        : {}),
      ...(cachedConcrete?.nbrOfLegsForEndStirrups !== undefined
        ? { nbrOfLegsForEndStirrups: Number(cachedConcrete.nbrOfLegsForEndStirrups) }
        : {}),
      ...(cachedConcrete?.stirrupsSlope !== undefined ? { stirrupsSlope: Number(cachedConcrete.stirrupsSlope) } : {}),
      ...(cachedConcrete?.codeControl !== undefined ? { codeControl: cachedConcrete.codeControl } : {}),
      // UI-owned fields
      concLst: gradeGuids,
      longDia: selectedBars,
      btmDia: selectedBars,
      topDia: selectedBars,
      linkDia: selectedStirrups,
      maxNumberOfBarsY: maxBarsY,
      maxNumberOfBarsZ: maxBarsZ,
      longDiaMtrlGuid: rebarMtrlGuid,
      btmDiaMtrlGuid: rebarMtrlGuid,
      topDiaMtrlGuid: rebarMtrlGuid,
      linkDiaMtrlGuid: stirrupMtrlGuid,
      minSectionWidth: minY / 1000,
      maxSectionWidth: maxY / 1000,
      minSectionHeight: minZ / 1000,
      maxSectionHeight: maxZ / 1000,
      detailingOptions: [
        symmetry === "biaxial"
          ? ng.endpoint_args.common_api_functions.DetailingOption.DETAILING_OPTION_BIAXIAL_SYMMETRY
          : ng.endpoint_args.common_api_functions.DetailingOption.DETAILING_OPTION_UNIAXIAL_SYMMETRY,
      ],
    });

    const settingsObj = ng.endpoint_args.common_api_functions.AutoDesignSettings.create({
      elementGuid: getElementGuid(selProject?.data),
      sectionGuids,
      limitUtilization,
      concrete: concreteSettings,
      column: ng.endpoint_args.common_api_functions.AutoDesignColumn.create({}),
    });

    const autoDesign = ng.endpoint_args.common_api_functions.AutoDesign.create({
      active: true,
      settings: [settingsObj],
      secDb: secDb.map((s) => ng.sections.Section.fromObject(s)),
      mtrlDb: mtrlDb.map((m) => ng.material.Data.fromObject(m)),
    });

    return ng.endpoint_args.common_api_functions.AutoDesign.toObject(autoDesign);
  };

  return (
    <Container>
      <Row style={{ marginTop: 16, marginBottom: 0, borderBottom: "1px solid lightgray" }}>
        <Col className="header sx-cd-section-title">
          <span className="header">{t("column_size_limits_.label", "Column size limits")}</span>
        </Col>
      </Row>

      <Row className="container-row">
        <Col className="editfield-label">
          {isCircularConcreteSection
            ? t("min_column_size_mm_.label", "Min column size (mm)")
            : t("min_column_size_y_mm_.label", "Min column size y (mm)")}
        </Col>
        <Col xs={12} className="d-flex align-items-center gap-2 ps-0 px-0" style={{ height: 32 }}>
          <SXEditField
            type="number"
            val={minY}
            textAlign="left"
            fullWidth
            disabled={isDesigning}
            onUpdate={(value) => setMinY(Number(value))}
          />
          <span className="sx-cd-range-separator">-</span>
          <SXEditField
            type="number"
            val={maxY}
            textAlign="left"
            fullWidth
            disabled={isDesigning}
            onUpdate={(value) => setMaxY(Number(value))}
          />
        </Col>
      </Row>

      {!isCircularConcreteSection && (
        <Row className="container-row">
          <Col className="editfield-label">{t("min_column_size_z_mm_.label", "Min column size z (mm)")}</Col>
          <Col xs={12} className="d-flex align-items-center gap-2 px-0" style={{ height: 32 }}>
            <SXEditField
              type="number"
              val={minZ}
              textAlign="left"
              fullWidth
              disabled={isDesigning}
              onUpdate={(value) => setMinZ(Number(value))}
            />
            <span className="sx-cd-range-separator">-</span>
            <SXEditField
              type="number"
              val={maxZ}
              textAlign="left"
              fullWidth
              disabled={isDesigning}
              onUpdate={(value) => setMaxZ(Number(value))}
            />
          </Col>
        </Row>
      )}

      <Row style={{ marginBottom: 16 }}>
        <Col className="editfield-label">
          {t("matching_sections_from_library_.label", "Matching sections from library")}
        </Col>
        <Col xs={12} className="sx-cd-chip-wrap sx-cd-chip-wrap--display sectionlib">
          {matchingSections.length > 0 ? (
            matchingSections.map((section) => renderDisplayChip(section.guid, section.name))
          ) : (
            <span className="sx-cd-no-match">{t("no_matching_sections_.label", "No matching sections")}</span>
          )}
        </Col>
      </Row>

      <Row style={{ marginTop: 16, marginBottom: 0, borderBottom: "1px solid lightgray" }}>
        <Col className="header sx-cd-section-title">{t("concrete_material_.label", "Concrete material")}</Col>
      </Row>

      <Row style={{ marginTop: 8 }}>
        <Col xs={12} className="editfield-label concretegrades">
          {t("concrete_grades_.label", "Concrete grades")}
        </Col>

        <Col xs={12} className="sx-cd-chip-wrap">
          {CONCRETE_GRADES.map((grade) =>
            renderSelectableChip(grade, grade, selectedGrades, setSelectedGrades, isDesigning),
          )}
        </Col>
      </Row>

      <Row style={{ marginTop: 16, marginBottom: 0, borderBottom: "1px solid lightgray" }}>
        <Col className="header sx-cd-section-title">{t("reinforcement_limits_.label", "Reinforcement limits")}</Col>
      </Row>

      <Row style={{ marginTop: 8 }}>
        <Col xs={12} className="editfield-label">
          {t("main_reinforcement_.label", "Main reinforcement")}
        </Col>
        <Col xs={12} className="sx-cd-chip-wrap mainreinforcement">
          {BAR_DIAMETERS_MM.map((diameter) =>
            renderSelectableChip(diameter, toDiameterLabel(diameter), selectedBars, setSelectedBars, isDesigning),
          )}
        </Col>
      </Row>

      <Row className="container-row align-items-center" style={{ marginTop: 8 }}>
        {!isCircularConcreteSection && (
          <Col xs={12} md={6}>
            <Row>
              <Col xs={12} className="editfield-label">
                {t("max_bars_in_y_.label", "Max bars in y")}
              </Col>
              <Col xs={12} className="ps-0">
                <SXEditField
                  type="number"
                  val={maxBarsY}
                  textAlign="left"
                  fullWidth
                  disabled={isDesigning}
                  onUpdate={(value) => setMaxBarsY(Number(value))}
                />
              </Col>
            </Row>
          </Col>
        )}
        <Col xs={12} md={isCircularConcreteSection ? 12 : 6}>
          <Row>
            <Col xs={12} className="editfield-label">
              {isCircularConcreteSection
                ? t("max_bars_in_x_.label", "Max bars")
                : t("max_bars_in_z_.label", "Max bars in z")}
            </Col>
            <Col xs={12} className="px-0">
              <SXEditField
                type="number"
                val={maxBarsZ}
                textAlign="left"
                fullWidth
                disabled={isDesigning || (!isCircularConcreteSection && symmetry === "biaxial")}
                onUpdate={(value) => setMaxBarsZ(Number(value))}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Row>
        <Col xs={12} className="editfield-label">
          {t("stirrups_.label", "Stirrups")}
        </Col>
        <Col xs={12} className="sx-cd-chip-wrap">
          {STIRRUP_DIAMETERS_MM.map((diameter) =>
            renderSelectableChip(
              diameter,
              toDiameterLabel(diameter),
              selectedStirrups,
              setSelectedStirrups,
              isDesigning,
            ),
          )}
        </Col>
      </Row>

      <Row style={{ marginTop: 16, marginBottom: 0, borderBottom: "1px solid lightgray" }}>
        <Col className="editfield-label sx-cd-section-title" style={{ marginBottom: 0 }}>
          {t("detailing_options_.label", "Detailing options")}
        </Col>
      </Row>
      <Row className="container-row align-items-center" style={{ marginTop: 8 }}>
        <Col className="editfield-label">{t("symmetry_.label", "Symmetry")}</Col>
        <Col xs={12} className="px-0">
          <SXDropdown
            id="sx-detailing-symmetry"
            name="sx-detailing-symmetry"
            lang=""
            list={symmetryOptions}
            val={selectedSymmetry}
            disabled={isDesigning || isCircularConcreteSection}
            onUpdate={(value) => {
              if (value) {
                setSymmetry(value.id);
              }
            }}
            localisation={false}
          />
        </Col>
        <Col xs={12} className="ps-0 px-0 mt-4 mb-4">
          <Col className="editfield-label">{t("limit_utilization_.label")} [%]</Col>
          <Col xs={12} className="px-0">
            <SXEditField
              type="number"
              val={limitUtilization}
              unit={RATIO_TO_PERCENT_UNIT}
              displayUnit={false}
              textAlign="left"
              fullWidth
              disabled={isDesigning}
              onUpdate={(value) => {
                // value arrives as a ratio — SXEditField has already applied fromInput.
                const ratio = Number(value);
                if (!Number.isFinite(ratio) || ratio < 0) {
                  return;
                }

                // Keep local input state and endpointCache.autoDesign in sync (always a ratio).
                setLimitUtilization(ratio);
                dispatch(ProjectSlice.actions.setAutoDesignLimitUtilization(ratio));
              }}
            />
          </Col>
        </Col>
      </Row>

      <hr />
      <Row className="pb-4">
        <Col xs={12} className="ps-0 px-0">
          <DesignRunButton isDesigning={isDesigning} onRun={() => runDesign(buildAutoDesign)} />
        </Col>
      </Row>
    </Container>
  );
};
